import type { FC, PropsWithChildren } from "react";
import { tv } from "tailwind-variants";

type Justify =
  | "center"
  | "end"
  | "flex-end"
  | "flex-start"
  | "start"
  | "space-around"
  | "space-between"
  | "space-evenly"
  | "stretch"
  | "baseline"
  | "normal";

type Align =
  | "center"
  | "end"
  | "flex-end"
  | "flex-start"
  | "start"
  | "baseline"
  | "stretch";

type Gap = number | string;

type FlexDirection = "column" | "column-reverse" | "row" | "row-reverse";

type FlexWrap = "nowrap" | "wrap" | "wrap-reverse";

export type DefaultProps = {
  justify?: Justify;
  align?: Align;
  gap?: Gap;
  flexDirection?: FlexDirection;
  flexWrap?: FlexWrap;
};

export type FlexProps = PropsWithChildren<
  DefaultProps & {
    className?: string;
    as?: "div" | "span" | "section" | "article" | "nav" | "ul";
    md?: DefaultProps;
    d?: DefaultProps;
  }
>;

const styleTransformer = (value: string | number): string => {
  return typeof value === "string" ? value : `${value}px`;
};

const directionStyle = (
  value: FlexDirection | undefined,
  breakpoint?: "md" | "lg",
): string => {
  if (!value) return "";

  const directionClass: Record<FlexDirection, string> = {
    column:
      breakpoint === "md"
        ? "md:flex-col"
        : breakpoint === "lg"
          ? "lg:flex-col"
          : "flex-col",
    "column-reverse":
      breakpoint === "md"
        ? "md:flex-col-reverse"
        : breakpoint === "lg"
          ? "lg:flex-col-reverse"
          : "flex-col-reverse",
    row:
      breakpoint === "md"
        ? "md:flex-row"
        : breakpoint === "lg"
          ? "lg:flex-row"
          : "flex-row",
    "row-reverse":
      breakpoint === "md"
        ? "md:flex-row-reverse"
        : breakpoint === "lg"
          ? "lg:flex-row-reverse"
          : "flex-row-reverse",
  };

  return directionClass[value];
};

const alignStyle = (
  value: Align | undefined,
  breakpoint?: "md" | "lg",
): string => {
  if (!value) return "";

  const alignClass: Record<Align, string> = {
    center:
      breakpoint === "md"
        ? "md:items-center"
        : breakpoint === "lg"
          ? "lg:items-center"
          : "items-center",
    start:
      breakpoint === "md"
        ? "md:items-start"
        : breakpoint === "lg"
          ? "lg:items-start"
          : "items-start",
    end:
      breakpoint === "md"
        ? "md:items-end"
        : breakpoint === "lg"
          ? "lg:items-end"
          : "items-end",
    "flex-start":
      breakpoint === "md"
        ? "md:items-start"
        : breakpoint === "lg"
          ? "lg:items-start"
          : "items-start",
    "flex-end":
      breakpoint === "md"
        ? "md:items-end"
        : breakpoint === "lg"
          ? "lg:items-end"
          : "items-end",
    stretch:
      breakpoint === "md"
        ? "md:items-stretch"
        : breakpoint === "lg"
          ? "lg:items-stretch"
          : "items-stretch",
    baseline:
      breakpoint === "md"
        ? "md:items-baseline"
        : breakpoint === "lg"
          ? "lg:items-baseline"
          : "items-baseline",
  };

  return alignClass[value];
};

const justifyStyle = (
  value: Justify | undefined,
  breakpoint?: "md" | "lg",
): string => {
  if (!value) return "";

  const justifyClass: Record<Justify, string> = {
    center:
      breakpoint === "md"
        ? "md:justify-center"
        : breakpoint === "lg"
          ? "lg:justify-center"
          : "justify-center",
    end:
      breakpoint === "md"
        ? "md:justify-end"
        : breakpoint === "lg"
          ? "lg:justify-end"
          : "justify-end",
    "flex-end":
      breakpoint === "md"
        ? "md:justify-end"
        : breakpoint === "lg"
          ? "lg:justify-end"
          : "justify-end",
    "flex-start":
      breakpoint === "md"
        ? "md:justify-start"
        : breakpoint === "lg"
          ? "lg:justify-start"
          : "justify-start",
    start:
      breakpoint === "md"
        ? "md:justify-start"
        : breakpoint === "lg"
          ? "lg:justify-start"
          : "justify-start",
    "space-around":
      breakpoint === "md"
        ? "md:justify-around"
        : breakpoint === "lg"
          ? "lg:justify-around"
          : "justify-around",
    "space-between":
      breakpoint === "md"
        ? "md:justify-between"
        : breakpoint === "lg"
          ? "lg:justify-between"
          : "justify-between",
    "space-evenly":
      breakpoint === "md"
        ? "md:justify-evenly"
        : breakpoint === "lg"
          ? "lg:justify-evenly"
          : "justify-evenly",
    stretch:
      breakpoint === "md"
        ? "md:justify-stretch"
        : breakpoint === "lg"
          ? "lg:justify-stretch"
          : "justify-stretch",
    baseline:
      breakpoint === "md"
        ? "md:justify-baseline"
        : breakpoint === "lg"
          ? "lg:justify-baseline"
          : "justify-baseline",
    normal:
      breakpoint === "md"
        ? "md:justify-normal"
        : breakpoint === "lg"
          ? "lg:justify-normal"
          : "justify-normal",
  };

  return justifyClass[value];
};

const flexWrapStyle = (
  value: FlexWrap | undefined,
  breakpoint?: "md" | "lg",
): string => {
  if (!value) return "";

  const flexWrapClass: Record<FlexWrap, string> = {
    wrap:
      breakpoint === "md"
        ? "md:flex-wrap"
        : breakpoint === "lg"
          ? "lg:flex-wrap"
          : "flex-wrap",
    nowrap:
      breakpoint === "md"
        ? "md:flex-nowrap"
        : breakpoint === "lg"
          ? "lg:flex-nowrap"
          : "flex-nowrap",
    "wrap-reverse":
      breakpoint === "md"
        ? "md:flex-wrap-reverse"
        : breakpoint === "lg"
          ? "lg:flex-wrap-reverse"
          : "flex-wrap-reverse",
  };

  return flexWrapClass[value];
};

export const Flex: FC<FlexProps> = ({
  md,
  d,
  className,
  align,
  justify,
  gap = 0,
  flexDirection,
  flexWrap,
  children,
  as,
}) => {
  const flexStyles = tv({
    base: `flex gap-[var(--gap)]  md:gap-[var(--mdGap)] lg:gap-[var(--dGap)]
     ${directionStyle(flexDirection)} ${directionStyle(md?.flexDirection ?? flexDirection, "md")} ${directionStyle(d?.flexDirection ?? md?.flexDirection ?? flexDirection, "lg")}  
     ${justifyStyle(justify)} ${justifyStyle(md?.justify ?? justify, "md")} ${justifyStyle(d?.justify ?? md?.justify ?? justify, "lg")}  
     ${alignStyle(align)} ${alignStyle(md?.align ?? align, "md")} ${alignStyle(d?.align ?? md?.align ?? align, "lg")}
     ${flexWrapStyle(flexWrap)} ${flexWrapStyle(md?.flexWrap ?? flexWrap, "md")} ${flexWrapStyle(d?.flexWrap ?? md?.flexWrap ?? flexWrap, "lg")}
     ${className}`,
  });

  const Root = as ?? "div";

  return (
    <Root
      className={flexStyles()}
      style={{
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        "--gap": styleTransformer(gap),
        "--mdGap": styleTransformer(md?.gap ?? gap),
        "--dGap": styleTransformer(d?.gap ?? md?.gap ?? gap),
      }}
    >
      {children}
    </Root>
  );
};
